import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Clock, BarChart3, ArrowLeft, ArrowRight, PlayCircle, Award, Download, BookOpen,
  Lock, Crown, CheckCircle2, ListVideo, Star, Heart, Bookmark, ChevronLeft, ChevronRight,
  Globe, FolderOpen, Users, Calendar, Infinity as InfinityIc, Headphones, RefreshCw, FileCheck,
} from 'lucide-react';
import { useLang, pickLocalized, tx } from '../i18n/LanguageContext';
import { usePageMeta } from '../lib/seo';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { getMergedCourses } from '../lib/courses';
import { useCourse, cacheCourses } from '../lib/courseCache';
import { CourseDetailSkeleton, CourseLoadState } from '../components/course/CourseLoadStates';
import { toYouTubeEmbed } from '../lib/youtube';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import LockedDialog, { hasAccess, FreeBadge } from '../components/LockedDialog';
import { useCategories } from '../hooks/useCategories';
import { CourseCard } from '../components/courses/CourseCard';
import { CourseHero } from '../components/course/CourseHero';
import { CurriculumList } from '../components/course/CurriculumList';
import { useCourseProgress } from '../lib/useCourseProgress';
import { buildCurriculum, countLabel, fmtDuration, lessonSecs } from '../lib/curriculum';
import { isHtml, safeHtml, stripHtml } from '../lib/richText';

const DEFAULT_LEARN = [
  ['أساسيات وبنية النظام', 'Fundamentals & system architecture'],
  ['تطبيقات عملية حقيقية', 'Real practical applications'],
  ['أمثلة شاملة وتمارين', 'Comprehensive examples & exercises'],
  ['استكشاف الأخطاء وإصلاحها', 'Troubleshooting & debugging'],
  ['أفضل الممارسات الصناعية', 'Industrial best practices'],
  ['مشاريع تطبيقية متكاملة', 'Complete hands-on projects'],
];
const learnOf = (course) => (course.learn?.length ? course.learn.map(p => [p.ar, p.en]) : DEFAULT_LEARN);

const toYouTubeId = (url) => {
  if (!url) return '';
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  return m ? m[1] : '';
};

const MOCK_REVIEWS = [
  {
    nameAr: 'محمد المتيري', nameEn: 'Mohammed Al-Mutairi',
    role: 'مهندس متمرّن', rating: 5,
    textAr: 'شرح ممتاز ومبسّط، والكورس شامل لكل ما أحتاجه في عملي اليومي.',
    textEn: 'Excellent and simple explanation, the course is comprehensive for everything I need in my daily work.',
    avatar: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=80',
  },
  {
    nameAr: 'سارة القحطاني', nameEn: 'Sarah Al-Qahtani',
    role: 'فني وحدة أنابيب', rating: 5,
    textAr: 'الكورس متقدم جداً والمشاريع العملية ساعدتني كثيراً في فهم البرنامج.',
    textEn: 'Very advanced course; the practical projects helped me a lot.',
    avatar: 'https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=80',
  },
  {
    nameAr: 'أحمد الدوسري', nameEn: 'Ahmed Al-Dosari',
    role: '', rating: 5,
    textAr: 'أفضل كورس SCADA حضرته، أنصح به لكل مهندس أو فني.',
    textEn: 'The best SCADA course I have attended; I recommend it to every engineer.',
    avatar: 'https://images.pexels.com/photos/697509/pexels-photo-697509.jpeg?auto=compress&cs=tinysrgb&w=80',
  },
];


const CourseDetail = () => {
  const { id } = useParams();
  const { t, lang, isRTL } = useLang();
  const { user } = useAuth();
  const { course, status, retry } = useCourse(id, user?.user_id);
  usePageMeta(course && pickLocalized(course.title, lang), course && (pickLocalized(course.subtitle, lang) || pickLocalized(course.description, lang)));
  const [courses, setCourses] = useState(getMergedCourses([]));
  const [showLock, setShowLock] = useState(false);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const navigate = useNavigate();
  const { completed, setDone } = useCourseProgress(id, user);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const playerRef = useRef(null);
  const tabsRef = useRef(null);
  const [tab, setTab] = useState('about');
  const [playing, setPlaying] = useState(false);
  const relatedRef = useRef(null);
  const Arrow = isRTL ? ArrowRight : ArrowLeft;
  const ChevronCta = isRTL ? ChevronLeft : ChevronRight;
  const { getLabel: getCategoryLabel } = useCategories();
  const subAccess = hasAccess(user);
  const L = (ar, en) => tx(lang, ar, en);

  useEffect(() => {
    api.get('/courses').then(({ data }) => { cacheCourses(data); setCourses(getMergedCourses(data)); }).catch(() => {});
  }, [user?.user_id]);
  useEffect(() => { setActiveLessonId(null); setPlaying(false); }, [id]);

  const canAccess = subAccess || !!course?.is_free;

  const lessons = useMemo(() => {
    if (!course) return [];
    const real = course.lessons || [];
    if (real.length > 0) return real;
    if (course.video_url || course.youtube || course.has_video) {
      return [{
        id: '__intro__',
        title: { ar: 'مقدمة الكورس', en: 'Course Introduction' },
        video_url: course.video_url || '',
        youtube: course.youtube || '',
        duration_min: 0,
        is_free: !!course.is_free,
      }];
    }
    return [];
  }, [course]);

  useEffect(() => {
    if (lessons.length > 0 && !activeLessonId) {
      setActiveLessonId((lessons.find(l => subAccess || l.is_free) || lessons[0]).id);
    }
  }, [lessons, activeLessonId, subAccess]);

  if (!course) return status === 'loading' ? <CourseDetailSkeleton /> : <CourseLoadState status={status} onRetry={retry} />;

  const activeLesson = activeLessonId === '__course_intro__'
    ? { id: '__course_intro__', title: course.title, video_url: course.video_url || '', youtube: course.youtube || '', is_free: true }
    : (lessons.find(l => l.id === activeLessonId) || lessons[0]);
  const hasAnyVideo = lessons.length > 0;
  const totalSeconds = lessons.reduce((s, l) => s + lessonSecs(l), 0);
  const progressPct = lessons.length > 0 ? Math.round((lessons.filter(l => completed.has(l.id)).length / lessons.length) * 100) : 0;
  const curriculum = buildCurriculum(course.sections, lessons, { ar: 'دروس الكورس', en: 'Course lessons' });
  const relatedCourses = courses.filter(c => c.id !== course.id).slice(0, 4);

  const playLesson = (lesson, idx) => {
    if (!canAccess && !lesson.is_free) {
      setShowLock(true);
      return;
    }
    setActiveLessonId(lesson.id);
    setPlaying(true);
    setTimeout(() => {
      playerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const v = playerRef.current?.querySelector('video');
      if (v) {
        try { v.play().catch(() => {}); } catch (e) { /* ignore */ }
      }
    }, 80);
  };

  const openLesson = (lesson) => {
    if (!canAccess && !lesson.is_free) { setShowLock(true); return; }
    navigate(`/courses/${course.id}/learn?lesson=${lesson.id}`);
  };

  const startCourse = () => {
    if (!canAccess && !lessons.some(l => l.is_free)) { setShowLock(true); return; }
    navigate(`/courses/${course.id}/learn`);
  };

  const playIntro = () => {
    if (course.video_url || course.youtube) {
      if (!canAccess) { setShowLock(true); return; }
      setActiveLessonId('__course_intro__');
      setPlaying(true);
      return;
    }
    const first = lessons.find(l => canAccess || l.is_free);
    if (!first) { setShowLock(true); return; }
    playLesson(first, 0);
  };

  const markComplete = () => {
    if (activeLesson && activeLesson.id !== '__course_intro__') setDone(activeLesson.id, true);
  };

  // Rating distribution (mock)
  const ratingDist = [
    { stars: 5, pct: 79 },
    { stars: 4, pct: 14 },
    { stars: 3, pct: 4 },
    { stars: 2, pct: 1 },
    { stars: 1, pct: 1 },
  ];

  const playerContent = (
    <>
              {!hasAnyVideo ? (
                <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">
                  {L('لا توجد دروس متاحة بعد', 'No lessons available yet')}
                </div>
              ) : !(canAccess || activeLesson?.is_free) ? (
                <button onClick={() => setShowLock(true)} className="w-full h-full relative group block" data-testid="locked-player-btn">
                  {course.image ? (
                    <img src={course.image} alt={pickLocalized(course.title, lang)} className="w-full h-full object-cover" />
                  ) : activeLesson?.youtube && toYouTubeId(activeLesson.youtube) ? (
                    <img src={`https://img.youtube.com/vi/${toYouTubeId(activeLesson.youtube)}/hqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-900/50 to-slate-50" />
                  )}
                  <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-colors" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="relative mb-2">
                      <div className="absolute inset-0 bg-blue-500/40 blur-2xl rounded-full" />
                      <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-blue-500 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                        <PlayCircle className="w-8 h-8 sm:w-12 sm:h-12 text-white" strokeWidth={1.8} />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center shadow-lg border-2 border-white">
                        <Lock className="w-3.5 h-3.5 text-slate-900" strokeWidth={3} />
                      </div>
                    </div>
                  </div>
                </button>
              ) : activeLesson?.video_url ? (
                <video
                  key={activeLesson.id}
                  src={activeLesson.video_url}
                  controls
                  controlsList="nodownload noplaybackrate noremoteplayback"
                  disablePictureInPicture
                  disableRemotePlayback
                  onContextMenu={(e) => e.preventDefault()}
                  onEnded={markComplete}
                  className="w-full h-full"
                  data-testid="course-video-html5"
                />
              ) : activeLesson?.youtube ? (
                <iframe
                  key={activeLesson.id}
                  src={toYouTubeEmbed(activeLesson.youtube)}
                  title={pickLocalized(activeLesson.title, lang)}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="course-video-yt"
                />
              ) : null}
    </>
  );

  return (
    <>
    <main data-testid="course-detail-page" className="page-y min-h-screen">
      <div className="site-container">
        <CourseHero
          course={course} lessons={lessons} totalSeconds={totalSeconds}
          canStart={canAccess || lessons.some(l => l.is_free)} playing={playing} player={playerContent} playerRef={playerRef}
          onStart={startCourse} onIntro={playIntro} onLocked={() => setShowLock(true)}
          onPreview={() => { setTab('content'); setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); }}
        />

        {/* Tabs */}
        <div ref={tabsRef} className="scroll-mt-28" />
        <Tabs value={tab} onValueChange={setTab} dir={isRTL ? 'rtl' : 'ltr'} className="w-full">
          <TabsList className="bg-white border border-slate-200 rounded-2xl p-1.5 flex flex-wrap h-auto justify-start gap-1 mb-6 overflow-x-auto shadow-sm">
            <TabsTrigger value="about" data-testid="tab-about" className="rounded-xl px-4 sm:px-6 h-10 font-bold data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-blue-600/25 text-xs sm:text-sm">{L('عن الكورس', 'About')}</TabsTrigger>
            <TabsTrigger value="learn" data-testid="tab-learn" className="rounded-xl px-4 sm:px-6 h-10 font-bold data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-blue-600/25 text-xs sm:text-sm">{L('ماذا ستتعلم', 'What you will learn')}</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content" className="rounded-xl px-4 sm:px-6 h-10 font-bold data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-blue-600/25 text-xs sm:text-sm">{L('محتوى الكورس', 'Course content')}</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews" className="rounded-xl px-4 sm:px-6 h-10 font-bold data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-blue-600/25 text-xs sm:text-sm">{L('تقييمات الطلاب', 'Reviews')}</TabsTrigger>
          </TabsList>

          {/* Two-column layout used inside each tab */}
          <TabsContent value="about" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6">
              <div className="lg:col-span-2 space-y-6 min-w-0">
                {/* About this course */}
                <section className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    {L('عن هذا الكورس', 'About this course')}
                  </h2>
                  {isHtml(pickLocalized(course.description, lang)) ? (
                    <div data-testid="course-description-html" className="rich-content text-slate-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: safeHtml(pickLocalized(course.description, lang)) }} />
                  ) : (
                  <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">
                    {pickLocalized(course.description, lang) || L('لا يوجد وصف متاح بعد', 'No description available yet.')}
                  </p>
                  )}
                </section>

                {/* What you will learn */}
                <section className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 mb-4 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    {L('ماذا ستتعلم في هذا الكورس', 'What you will learn')}
                  </h2>
                  <ul className="grid sm:grid-cols-2 gap-2.5 sm:gap-3">
                    {learnOf(course).map(([ar, en], i) => (
                      <li key={i} className={`flex items-start gap-2 text-xs sm:text-sm text-slate-700`}>
                        <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                        <span>{L(ar, en)}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                {/* Meta info */}
                <section className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-xs sm:text-sm">
                    {[
                      { Ic: Award,      label: L('المتطلبات', 'Prerequisites'),    value: L('لا توجد متطلبات مسبقة', 'No prerequisites') },
                      { Ic: BarChart3,  label: L('المستوى', 'Level'),               value: pickLocalized(course.level, lang) || L('متوسط', 'Intermediate') },
                      { Ic: FolderOpen, label: L('الفئة', 'Category'),              value: (course.categories?.[0] ? getCategoryLabel(course.categories[0], lang) : L('عام', 'General')) },
                      { Ic: Globe,      label: L('اللغة', 'Language'),              value: L('العربية', 'Arabic') },
                      ...(course.students > 0 ? [{ Ic: Users, label: L('الطلاب المسجلين', 'Enrolled students'), value: `${Number(course.students).toLocaleString('en-US')} ${L('طالب', 'students')}` }] : []),
                      { Ic: Calendar,   label: L('آخر تحديث', 'Last updated'),     value: (course.created_at || '').slice(0, 7) || L('مايو 2024', 'May 2024') },
                    ].map((row, i) => (
                      <div key={i} className={`flex items-center gap-3`}>
                        <row.Ic className="w-4 h-4 text-blue-600 shrink-0" />
                        <div className="flex-1 flex items-center justify-between gap-2">
                          <span className="text-slate-900 font-semibold truncate">{row.value}</span>
                          <span className="text-slate-500 text-[11px] truncate">{row.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

              </div>

              {/* Sidebar: course content + progress */}
              <aside className="space-y-4 lg:sticky lg:top-24 self-start">
                {/* Course content list */}
                <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="lessons-list">
                  <div className={`px-5 py-4 border-b border-slate-200 flex items-center gap-2`}>
                    <ListVideo className="w-5 h-5 text-blue-600" />
                    <div className="flex-1">
                      <h3 className="text-slate-900 font-extrabold text-sm sm:text-base">{L('محتوى الكورس', 'Course content')}</h3>
                      <p className="text-slate-500 text-[11px] sm:text-xs">
                        {countLabel(lang, lessons.length, ['درس واحد', 'درسان', 'دروس', 'درساً'], 'lessons')}
                        {totalSeconds > 0 && ` · ${fmtDuration(totalSeconds, lang)}`}
                      </p>
                    </div>
                  </div>
                  <div className="max-h-[460px] overflow-y-auto">
                    {lessons.length === 0 ? (
                      <div className="px-5 py-8 text-center text-slate-500 text-sm">
                        {L('لم تتم إضافة دروس بعد', 'No lessons added yet')}
                      </div>
                    ) : <CurriculumList groups={curriculum.groups} activeId={null} completed={completed} isLocked={l => !(canAccess || l.is_free)} onSelect={openLesson} />}
                  </div>
                  <div className="px-4 py-3 border-t border-slate-200">
                    <Button data-testid="sidebar-start-learning-btn" onClick={startCourse} variant="outline" className="w-full border-blue-200 text-blue-700 hover:bg-slate-100 h-10 text-xs sm:text-sm">
                      <PlayCircle className={`w-4 h-4 ${isRTL ? 'ms-2' : 'me-2'}`} />
                      {L('افتح صفحة الدروس', 'Open lessons page')}
                    </Button>
                  </div>
                </div>

                {/* Progress card (only when user has access) */}
                {canAccess && lessons.length > 0 && (
                  <div className="rounded-2xl bg-white border border-slate-200 p-5 text-center">
                    <div className="text-slate-600 text-xs sm:text-sm font-semibold mb-3">{L('تقدمك في الكورس', 'Your progress')}</div>
                    <div className="relative w-24 h-24 mx-auto mb-2">
                      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="44" stroke="#1e3a8a40" strokeWidth="8" fill="none" />
                        <circle cx="50" cy="50" r="44" stroke="#3b82f6" strokeWidth="8" fill="none"
                          strokeDasharray={`${(progressPct / 100) * 276.46} 276.46`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center text-slate-900 font-extrabold text-xl">{progressPct}%</div>
                    </div>
                    <div className="text-slate-500 text-[11px] sm:text-xs mb-3">
                      {L(`الدرس ${Math.min(completed.size + 1, lessons.length)} من ${lessons.length}`, `Lesson ${Math.min(completed.size + 1, lessons.length)} of ${lessons.length}`)}
                    </div>
                    <Button onClick={startCourse} className="w-full btn-primary-blue border-0 h-10 text-xs sm:text-sm font-bold">
                      <PlayCircle className={`w-4 h-4 ${isRTL ? 'ms-2' : 'me-2'}`} />
                      {L('متابعة التعلم', 'Continue learning')}
                    </Button>
                  </div>
                )}
              </aside>
            </div>
          </TabsContent>

          <TabsContent value="learn" className="mt-0">
            <div className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
              <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 mb-4">{L('ماذا ستتعلم في هذا الكورس', 'What you will learn')}</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {learnOf(course).map(([ar, en], i) => (
                  <li key={i} className={`flex items-start gap-2 text-sm text-slate-700`}>
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                    <span>{L(ar, en)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="content" className="mt-0">
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200">
                <h3 className="text-slate-900 font-extrabold">{L('محتوى الكورس', 'Course content')}</h3>
                <p className="text-slate-500 text-xs">{lessons.length} {L('دروس', 'lessons')}</p>
              </div>
              <CurriculumList groups={curriculum.groups} activeId={null} completed={completed} isLocked={l => !(canAccess || l.is_free)} onSelect={openLesson} />
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="mt-0">
            <div className="grid lg:grid-cols-2 gap-5 lg:gap-6">
              {/* Rating summary */}
              <div className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6 text-center">
                <div className="text-4xl sm:text-5xl font-extrabold text-slate-900">4.8</div>
                <div className="flex items-center justify-center gap-1 my-2">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />)}
                </div>
                <div className="text-slate-500 text-sm mb-4">(128 {L('تقييم', 'ratings')})</div>
                <div className="space-y-2">
                  {ratingDist.map(r => (
                    <div key={r.stars} className={`flex items-center gap-3`}>
                      <div className="text-xs text-slate-600 w-12 shrink-0" dir="ltr">{r.stars} {L('نجوم', 'stars')}</div>
                      <div className="flex-1 h-2 rounded-full bg-blue-50 overflow-hidden">
                        <div className="h-full bg-amber-400" style={{ width: `${r.pct}%` }} />
                      </div>
                      <div className="text-xs text-slate-500 w-10 text-end shrink-0">{r.pct}%</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reviews list */}
              <div className="space-y-3">
                {MOCK_REVIEWS.map((r, i) => (
                  <div key={i} className={`rounded-xl bg-white border border-slate-200 p-4 flex items-start gap-3`}>
                    <img src={r.avatar} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className={`flex items-center gap-2`}>
                        <div className="text-slate-900 font-bold text-sm">{L(r.nameAr, r.nameEn)}</div>
                        <div className="flex">
                          {Array.from({ length: r.rating }).map((_, j) => <Star key={j} className="w-3 h-3 text-amber-400 fill-amber-400" />)}
                        </div>
                      </div>
                      <p className="text-slate-600 text-xs sm:text-sm mt-1 leading-relaxed">{L(r.textAr, r.textEn)}</p>
                    </div>
                  </div>
                ))}
                <Button variant="outline" className="w-full border-blue-200 text-blue-700 hover:bg-slate-100 h-11 text-sm">
                  {L('عرض جميع التقييمات', 'Show all reviews')}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Related courses */}
        {relatedCourses.length > 0 && (
          <section className="mt-8 sm:mt-10 rounded-2xl bg-white border border-slate-200 p-4 sm:p-6 relative">
            <div className={`flex items-center justify-between mb-4 sm:mb-5`}>
              <h2 className="text-lg sm:text-2xl font-extrabold text-slate-900">{L('كورسات قد تهمك', 'You may also like')}</h2>
              <Link to="/courses" className="text-blue-600 hover:text-blue-700 text-xs sm:text-sm font-medium inline-flex items-center gap-1">
                {L('عرض جميع الكورسات', 'View all')}
                <ChevronCta className="w-4 h-4" />
              </Link>
            </div>

            {/* Scroll arrows (desktop only) */}
            <button
              type="button"
              aria-label="prev"
              onClick={() => relatedRef.current?.scrollBy({ left: isRTL ? 320 : -320, behavior: 'smooth' })}
              className="hidden lg:flex absolute -left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 items-center justify-center shadow-lg z-10"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              aria-label="next"
              onClick={() => relatedRef.current?.scrollBy({ left: isRTL ? -320 : 320, behavior: 'smooth' })}
              className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 items-center justify-center shadow-lg z-10"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <div
              ref={relatedRef}
              className="flex gap-3 sm:gap-5 overflow-x-auto pt-2 pb-3 snap-x snap-mandatory scrollbar-hide"
              style={{ scrollbarWidth: 'none' }}
            >
              {relatedCourses.map(c => (
                <div key={c.id} className="snap-start shrink-0 w-[85%] sm:w-[300px] lg:w-[calc((100%-2.5rem)/3)]">
                  <CourseCard course={c} onOpen={(id) => navigate(`/courses/${id}`)} />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
    <LockedDialog open={showLock} onClose={() => setShowLock(false)} />
    </>
  );
};

export default CourseDetail;
